/**
 * Chatbot GraphQL Operations
 *
 * Inline GraphQL documents for chatbot settings, scenarios, and chat sessions.
 * These were previously in separate .graphql files with codegen, but are now
 * defined inline since the AI engine moved to the backend.
 */

import { gql } from 'graphql-tag'

// ─── Chatbot Settings ────────────────────────────────────────────────

export const GetChatbotSettingsDocument = gql`
  query GetChatbotSettings($tenantId: uuid!) {
    chatbot_settings(where: { tenant_id: { _eq: $tenantId } }, limit: 1) {
      id
      tenant_id
      enabled
      bot_display_name
      bot_avatar_type
      bot_avatar_file_id
      greeting_message_en
      greeting_message_ar
      personality_preset
      custom_tone_instructions
      supported_languages
      primary_language
      auto_suggest_alternatives
      auto_suggest_multiple_rooms
      proactive_recommendations
      max_response_tokens
      temperature
      operating_mode
      business_hours_start
      business_hours_end
      business_hours_timezone
      after_hours_message_en
      after_hours_message_ar
      min_advance_booking_days
      max_guests_per_booking
      auto_escalate_after_minutes
      escalation_keywords
      additional_instructions
      restricted_topics
      widget_allowed_scenarios
      retention_enabled
      max_ai_discount_percent
      retention_max_attempts
      ota_ai_enabled
      ota_max_response_chars
      ai_model
      default_guest_adults
      default_guest_children
      custom_base_prompt
      pre_arrival_template_en
      pre_arrival_template_ar
      checkout_reminder_template_en
      checkout_reminder_template_ar
      post_stay_template_en
      post_stay_template_ar
      whatsapp_include_emojis
      whatsapp_footer_text
      created_at
      updated_at
    }
  }
`

export const UpsertChatbotSettingsDocument = gql`
  mutation UpsertChatbotSettings($object: chatbot_settings_insert_input!) {
    insert_chatbot_settings_one(
      object: $object
      on_conflict: {
        constraint: chatbot_settings_tenant_unique
        update_columns: [
          enabled
          bot_display_name
          bot_avatar_type
          bot_avatar_file_id
          greeting_message_en
          greeting_message_ar
          personality_preset
          custom_tone_instructions
          supported_languages
          primary_language
          auto_suggest_alternatives
          auto_suggest_multiple_rooms
          proactive_recommendations
          max_response_tokens
          temperature
          operating_mode
          business_hours_start
          business_hours_end
          business_hours_timezone
          after_hours_message_en
          after_hours_message_ar
          min_advance_booking_days
          max_guests_per_booking
          auto_escalate_after_minutes
          escalation_keywords
          additional_instructions
          restricted_topics
          widget_allowed_scenarios
          retention_enabled
          max_ai_discount_percent
          retention_max_attempts
          ota_ai_enabled
          ota_max_response_chars
          ai_model
          default_guest_adults
          default_guest_children
          custom_base_prompt
          pre_arrival_template_en
          pre_arrival_template_ar
          checkout_reminder_template_en
          checkout_reminder_template_ar
          post_stay_template_en
          post_stay_template_ar
          whatsapp_include_emojis
          whatsapp_footer_text
        ]
      }
    ) {
      id
    }
  }
`

/** Type for chatbot settings fields - matches the query result shape */
export interface ChatbotSettingsFields {
  id: string
  tenant_id: string
  enabled: boolean | null
  bot_display_name: string | null
  bot_avatar_type: string | null
  bot_avatar_file_id: string | null
  greeting_message_en: string | null
  greeting_message_ar: string | null
  personality_preset: string | null
  custom_tone_instructions: string | null
  supported_languages: unknown
  primary_language: string | null
  auto_suggest_alternatives: boolean | null
  auto_suggest_multiple_rooms: boolean | null
  proactive_recommendations: boolean | null
  max_response_tokens: number | null
  temperature: unknown
  operating_mode: string | null
  business_hours_start: string | null
  business_hours_end: string | null
  business_hours_timezone: string | null
  after_hours_message_en: string | null
  after_hours_message_ar: string | null
  min_advance_booking_days: number | null
  max_guests_per_booking: number | null
  auto_escalate_after_minutes: number | null
  escalation_keywords: unknown
  additional_instructions: string | null
  restricted_topics: unknown
  widget_allowed_scenarios: unknown
  retention_enabled: boolean | null
  max_ai_discount_percent: unknown
  retention_max_attempts: number | null
  ota_ai_enabled: boolean | null
  ota_max_response_chars: number | null
  ai_model: string | null
  default_guest_adults: number | null
  default_guest_children: number | null
  custom_base_prompt: string | null
  pre_arrival_template_en: string | null
  pre_arrival_template_ar: string | null
  checkout_reminder_template_en: string | null
  checkout_reminder_template_ar: string | null
  post_stay_template_en: string | null
  post_stay_template_ar: string | null
  whatsapp_include_emojis: boolean | null
  whatsapp_footer_text: string | null
  created_at: string | null
  updated_at: string | null
}

// ─── Chatbot Scenarios ───────────────────────────────────────────────

export const GetChatbotScenariosDocument = gql`
  query GetChatbotScenarios($tenant_id: uuid!) {
    chatbot_scenarios(
      where: { _or: [{ tenant_id: { _eq: $tenant_id } }, { is_system: { _eq: true } }] }
      order_by: { sort_order: asc }
    ) {
      id
      key
      name_en
      name_ar
      description_en
      description_ar
      icon
      is_active
      is_system
      tool_modules
      custom_prompt_instructions
      sort_order
      created_at
      updated_at
    }
  }
`

export const GetActiveChatbotScenariosDocument = gql`
  query GetActiveChatbotScenarios($tenant_id: uuid!) {
    chatbot_scenarios(
      where: {
        _or: [{ tenant_id: { _eq: $tenant_id } }, { is_system: { _eq: true } }]
        is_active: { _eq: true }
      }
      order_by: { sort_order: asc }
    ) {
      id
      key
      name_en
      name_ar
      description_en
      icon
      is_system
      tool_modules
    }
  }
`

export const UpdateChatbotScenarioDocument = gql`
  mutation UpdateChatbotScenario(
    $id: uuid!
    $is_active: Boolean
    $custom_prompt_instructions: String
    $name_en: String
    $name_ar: String
    $description_en: String
    $icon: String
    $tool_modules: jsonb
    $sort_order: Int
  ) {
    update_chatbot_scenarios_by_pk(
      pk_columns: { id: $id }
      _set: {
        is_active: $is_active
        custom_prompt_instructions: $custom_prompt_instructions
        name_en: $name_en
        name_ar: $name_ar
        description_en: $description_en
        icon: $icon
        tool_modules: $tool_modules
        sort_order: $sort_order
      }
    ) {
      id
    }
  }
`

export const ToggleChatbotScenarioDocument = gql`
  mutation ToggleChatbotScenario($id: uuid!, $is_active: Boolean!) {
    update_chatbot_scenarios_by_pk(
      pk_columns: { id: $id }
      _set: { is_active: $is_active }
    ) {
      id
      is_active
    }
  }
`

export const UpsertChatbotScenarioDocument = gql`
  mutation UpsertChatbotScenario(
    $key: String!
    $name_en: String!
    $name_ar: String
    $description_en: String
    $description_ar: String
    $icon: String
    $is_active: Boolean!
    $is_system: Boolean!
    $tool_modules: jsonb!
    $custom_prompt_instructions: String
    $sort_order: Int
  ) {
    insert_chatbot_scenarios_one(
      object: {
        key: $key
        name_en: $name_en
        name_ar: $name_ar
        description_en: $description_en
        description_ar: $description_ar
        icon: $icon
        is_active: $is_active
        is_system: $is_system
        tool_modules: $tool_modules
        custom_prompt_instructions: $custom_prompt_instructions
        sort_order: $sort_order
      }
      on_conflict: {
        constraint: chatbot_scenarios_pkey
        update_columns: [
          name_en
          name_ar
          description_en
          description_ar
          icon
          is_active
          tool_modules
          custom_prompt_instructions
          sort_order
        ]
      }
    ) {
      id
    }
  }
`

export const DeleteChatbotScenarioDocument = gql`
  mutation DeleteChatbotScenario($id: uuid!) {
    delete_chatbot_scenarios_by_pk(id: $id) {
      id
    }
  }
`

/** Type for chatbot scenario fields - matches the query result shape */
export interface ChatbotScenarioFields {
  id: string
  key: string
  name_en: string
  name_ar: string | null
  description_en: string | null
  description_ar: string | null
  icon: string | null
  is_active: boolean
  is_system: boolean
  tool_modules: string[]
  custom_prompt_instructions: string | null
  sort_order: number | null
  created_at: string | null
  updated_at: string | null
}

// ─── Personality Presets ─────────────────────────────────────────────

export const GetPersonalityPresetsDocument = gql`
  query GetPersonalityPresets {
    chatbot_personality_presets(order_by: { sort_order: asc }) {
      key
      label
      description
      sort_order
    }
  }
`

export interface PersonalityPreset {
  key: string
  label: string
  description: string | null
  sort_order: number | null
}

// ─── Chat Sessions (Live Chat) ──────────────────────────────────────

export const LiveChatSessionsDocument = gql`
  subscription LiveChatSessions($tenantId: uuid!) {
    chat_sessions(
      where: {
        tenant_id: { _eq: $tenantId }
        status: { _in: ["active", "staff_handling"] }
      }
      order_by: { updated_at: desc }
    ) {
      id
      session_code
      tenant_id
      property_id
      reservation_id
      language
      ai_enabled
      needs_attention
      priority
      status
      created_at
      updated_at
      property {
        id
        name_en
      }
      reservation {
        id
        guest {
          id
          first_name_en
          last_name_en
        }
      }
      messages(limit: 1, order_by: { created_at: desc }) {
        id
        content
        role
        created_at
      }
    }
  }
`

export const LiveChatMessagesDocument = gql`
  subscription LiveChatMessages($sessionId: uuid!) {
    chat_messages(
      where: { chat_session_id: { _eq: $sessionId } }
      order_by: { created_at: asc }
    ) {
      id
      chat_session_id
      role
      content
      sender_type
      staff_name
      created_at
    }
  }
`

export const LiveChatTakeoverSessionDocument = gql`
  mutation LiveChatTakeoverSession($sessionId: uuid!, $staffId: uuid!) {
    update_chat_sessions_by_pk(
      pk_columns: { id: $sessionId }
      _set: {
        ai_enabled: false
        status: "staff_handling"
        assigned_staff_id: $staffId
      }
    ) {
      id
      ai_enabled
      status
    }
  }
`

export const LiveChatHandbackToAiDocument = gql`
  mutation LiveChatHandbackToAi($sessionId: uuid!) {
    update_chat_sessions_by_pk(
      pk_columns: { id: $sessionId }
      _set: {
        ai_enabled: true
        status: "active"
        assigned_staff_id: null
      }
    ) {
      id
      ai_enabled
      status
    }
  }
`

export const LiveChatSendStaffMessageDocument = gql`
  mutation LiveChatSendStaffMessage(
    $sessionId: uuid!
    $content: String!
    $staffId: uuid!
    $staffName: String!
  ) {
    insert_chat_messages_one(
      object: {
        chat_session_id: $sessionId
        role: "assistant"
        content: $content
        sender_type: "staff"
        staff_id: $staffId
        staff_name: $staffName
      }
    ) {
      id
    }
  }
`

// ─── Guest Chat Messages (Subscription for widget) ──────────────────

export const GuestChatMessagesDocument = gql`
  subscription GuestChatMessages($sessionId: uuid!) {
    chat_messages(
      where: { chat_session_id: { _eq: $sessionId } }
      order_by: { created_at: asc }
    ) {
      id
      role
      content
      sender_type
      staff_name
      created_at
    }
  }
`

// ─── Reservation AI Chat Sessions ───────────────────────────────────

export const ChatbotGetSessionsForReservationDocument = gql`
  query ChatbotGetSessionsForReservation($reservationId: uuid!) {
    chat_sessions(
      where: { reservation_id: { _eq: $reservationId } }
      order_by: { created_at: desc }
    ) {
      id
      session_code
      language
      status
      created_at
      messages(order_by: { created_at: asc }) {
        id
        role
        content
        sender_type
        staff_name
        created_at
      }
    }
  }
`
